import {
	useGetLastLoginQuery,
	useGetUserProfileQuery,
	useUpdateUserProfileMutation,
} from "@api/accountManagement";
import enums from "@enums/index.js";
import { showAlert } from "@components/alert/action";
import { showBettoAlert } from "@layouts/betto/components/Alert/showBettoAlert";
import { BettoProfileAvatar } from "../../../components/BettoProfileAvatar";
import { calculateTimeOnPlatform } from "@pages/profile-v2/utils/index.js";
import { BettoAccountDetailsForm } from "../../../components/BettoAccountDetailsForm";
import { copyToClipboard, setUser } from "@utils/index.js";
import { setNationalIdInMemory } from "@utils/userPiiMemory.js";
import moment from "moment";
import React, { memo, useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { BettoCard } from "../../../components/BettoCard";
import { BettoPrimaryButton } from "../../../components/BettoButton";
import {
	AvatarWrapper,
	CardHeaderRow,
	ContactField,
	CopyLink,
	FieldInput,
	FieldInputRow,
	FieldLabel,
	PhoneHint,
	ProfileDetails,
	ProfileRow,
	UserIdRow,
	UserMeta,
	Username,
} from "./elements";

const selectAuthUser = (state) => state.auth.user;

const parseUserId = (userId) => {
	if (!userId) return "";
	if (userId.length <= 17) return userId;
	return `${userId.substring(0, 14)}...`;
};

export const AccountInfoSection = memo(({ translate }) => {
	const dispatch = useDispatch();
	const authUser = useSelector(selectAuthUser);
	const [isEditing, setIsEditing] = useState(false);

	const { data: userInfo, refetch: refetchUserInfo } = useGetUserProfileQuery();
	const { data: lastLoginData } = useGetLastLoginQuery();
	const [updateUserProfileMutation] = useUpdateUserProfileMutation();

	const updateProfile = authUser?.updateProfile ?? false;
	const hasEditableProfileFields =
		updateProfile ||
		!userInfo?.email ||
		(window.PROFILE_HAS_NATIONAL_ID && !userInfo?.national_id);

	useEffect(() => {
		if (updateProfile) {
			setIsEditing(true);
		}
	}, [updateProfile]);

	const handleSaveUserInfo = useCallback(
		async (payload) => {
			try {
				await updateUserProfileMutation(payload).unwrap();

				const updatedUser = { ...authUser, updateProfile: false };
				const nationalIdAttr = payload?.UserAttributes?.find(
					(a) => a.Name === "custom:nationalId"
				);

				if (nationalIdAttr?.Value) {
					setNationalIdInMemory(nationalIdAttr.Value);
				}

				dispatch({ type: enums.LOGIN_SUCCESS, payload: updatedUser });
				setUser(updatedUser);
				showBettoAlert(
					(alertPayload) => dispatch(showAlert(alertPayload)),
					"profile-updated"
				);
				refetchUserInfo();
				setIsEditing(false);
			} catch (error) {
				const errorCode = error?.data?.code || error?.error?.code;
				showBettoAlert(
					(alertPayload) => dispatch(showAlert(alertPayload)),
					"something-went-wrong",
					{
						msg:
							errorCode === "EmailLinkedToSocialIdentity"
								? "EmailLinkedToSocialIdentity"
								: "GENERIC_ERROR",
					}
				);
			}
		},
		[authUser, dispatch, refetchUserInfo, updateUserProfileMutation]
	);

	const usernameLetter = userInfo?.username?.charAt(0).toUpperCase();

	const formatLastLoginTime = (timestamp) => {
		if (!timestamp) return translate("Never");
		return moment.unix(timestamp).format("M/D/YYYY, h:mm:ss A");
	};

	return (
		<>
			<BettoCard title={translate("betto.profile.profileInfo")}>
				<CardHeaderRow>
					<ProfileRow>
						<AvatarWrapper>
							<BettoProfileAvatar
								letter={usernameLetter}
								size={72}
								showLetter={window.PROFILE_ICON_HAS_LETTER}
							/>
						</AvatarWrapper>
						<ProfileDetails>
							<Username>{userInfo?.username}</Username>
							<UserIdRow>
								<UserMeta>
									{translate("betto.profile.userId")}: {parseUserId(userInfo?.id)}
								</UserMeta>
								<CopyLink
									type="button"
									onClick={() => copyToClipboard(userInfo?.id, translate)}>
									{translate("betto.profile.copy")}
								</CopyLink>
							</UserIdRow>
							<UserMeta>
								{calculateTimeOnPlatform(userInfo?.create_date, translate)}
							</UserMeta>
						</ProfileDetails>
					</ProfileRow>
					<BettoPrimaryButton
						size="sm"
						onClick={() => setIsEditing((prev) => !prev)}
						disabled={!hasEditableProfileFields && !isEditing}>
						{isEditing
							? translate("betto.profile.cancel")
							: translate("betto.profile.edit")}
					</BettoPrimaryButton>
				</CardHeaderRow>
			</BettoCard>

			<BettoCard title={translate("betto.profile.contactInfo")}>
				<ContactField>
					<FieldLabel>{translate("E-mail Verification")}</FieldLabel>
					<FieldInputRow>
						<FieldInput
							value={userInfo?.email || ""}
							disabled
							readOnly
						/>
						{!userInfo?.email && (
							<BettoPrimaryButton
								size="sm"
								onClick={() => setIsEditing(true)}>
								{translate("betto.profile.verify")}
							</BettoPrimaryButton>
						)}
					</FieldInputRow>
				</ContactField>
				<ContactField>
					<FieldLabel>{translate("Phone Number")}</FieldLabel>
					<FieldInputRow>
						{userInfo?.phone_number ? (
							<FieldInput
								value={userInfo.phone_number}
								disabled
								readOnly
							/>
						) : (
							<PhoneHint>{translate("betto.profile.phoneHint")}</PhoneHint>
						)}
					</FieldInputRow>
				</ContactField>
			</BettoCard>

			{isEditing && hasEditableProfileFields && (
				<BettoCard title={translate("betto.profile.editProfile")}>
					<BettoAccountDetailsForm
						key={`account-form-${updateProfile}`}
						userInfo={userInfo}
						translate={translate}
						onSave={handleSaveUserInfo}
						updateProfile={updateProfile}
					/>
				</BettoCard>
			)}

			{lastLoginData && (
				<BettoCard title={translate("betto.profile.lastLogin")}>
					<UserMeta>
						{translate("Last Login")}:{" "}
						{formatLastLoginTime(lastLoginData.lastLoginTime)}
					</UserMeta>
					<UserMeta>
						{translate("IP Address")}: {lastLoginData.lastLoginIp || "-"}
					</UserMeta>
				</BettoCard>
			)}
		</>
	);
});

AccountInfoSection.displayName = "AccountInfoSection";

AccountInfoSection.propTypes = {
	translate: PropTypes.func.isRequired,
};
